import React, { useState, useMemo } from "react";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";

function RuleGlyph({ type = "mail" }) {
  if (type === "class") {
    return (
      <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
        <path
          d="M6.5 7.5h11v10h-11z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M9 5.5v2M15 5.5v2M9 12h6M9 15h4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "person") {
    return (
      <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
        <circle cx="12" cy="8.5" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M6.5 18c1-3 3.2-4.5 5.5-4.5s4.5 1.5 5.5 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (type === "system") {
    return (
      <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
        <path
          d="M12 8.5A3.5 3.5 0 1 1 12 15.5A3.5 3.5 0 0 1 12 8.5Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M19 12a7.8 7.8 0 0 0-.1-1l2-1.6-2-3.4-2.5 1a8.8 8.8 0 0 0-1.7-1L14.4 3h-4.8l-.3 2.9a8.8 8.8 0 0 0-1.7 1l-2.5-1-2 3.4 2 1.6a7.8 7.8 0 0 0 0 2.1l-2 1.6 2 3.4 2.5-1a8.8 8.8 0 0 0 1.7 1l.3 2.9h4.8l.3-2.9a8.8 8.8 0 0 0 1.7-1l2.5 1 2-3.4-2-1.6c.1-.3.1-.7.1-1Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "idle") {
    return (
      <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M12 8v4l2.5 2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "streak") {
    return (
      <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
        <path
          d="M12.5 3.8c.2 2.1-.6 3.8-2.1 5.5-1.2 1.3-1.8 2.4-1.8 4a4.4 4.4 0 0 0 8.8 0c0-2.7-1.8-4.2-3-5.6-.8-.9-1.5-2-1.9-3.9Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10.2 15.4c.6.8 1.3 1.2 2.2 1.2 1 0 1.7-.4 2.3-1.3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-rules-glyph" aria-hidden="true">
      <path
        d="M5 8.5 12 13l7-4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 6.5h11A1.5 1.5 0 0 1 19 8v8a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 16V8a1.5 1.5 0 0 1 1.5-1.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      className={`ac-rules-toggle ${checked ? "is-on" : ""} ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className={`ac-rules-toggle-knob ${checked ? "is-on" : ""}`} />
    </button>
  );
}

function FixedBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-extrabold"
      style={{
        background: "rgba(174,223,247,0.22)",
        borderColor: "rgba(109,184,232,0.28)",
        color: "#b1b9c0",
      }}
    >
      Pflichtregel
    </span>
  );
}

function RuleCard({ rule, onToggle, onDelete }) {
  return (
    <div className="ac-soft-panel ac-rules-card">
      <div className="ac-rules-card-grid">
        <div className="ac-rules-card-main">
          <div className="ac-rules-card-head">
            <div className="ac-rules-card-icon">
              <RuleGlyph type={rule.iconType} />
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="ac-rules-card-title">{rule.title}</h3>
                {rule.fixed && <FixedBadge />}
              </div>

              <p
                className="ac-rules-card-text"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textAlign: "left",
                }}
              >
                {rule.description}
              </p>

              <div className="mt-3 text-sm font-bold text-ac-muted">
                Erstellt von: {rule.createdBy || "System"}
              </div>
            </div>
          </div>
        </div>

        <div className="ac-rules-card-side">
          <div className="ac-rules-side-label">E-Mail-Benachrichtigung</div>

          <div className="ac-rules-side-toggle">
            <Toggle
              checked={rule.enabled}
              onChange={onToggle}
              disabled={rule.fixed}
            />
            <span className={`ac-rules-side-state ${rule.enabled ? "is-on" : ""}`}>
              {rule.enabled ? "Aktiv" : "Inaktiv"}
            </span>
          </div>

          {rule.fixed ? (
            <div className="mt-4 text-center text-xs font-bold text-ac-muted">
              Diese Regel ist systemrelevant und bleibt immer aktiv.
            </div>
          ) : (
            <button
              type="button"
              onClick={onDelete}
              className="ac-ghostbtn ac-rules-deletebtn"
            >
              Regel entfernen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewRuleForm({ form, setForm, onSave, onCancel }) {
  const update = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

  return (
    <div className="ac-soft-panel ac-rules-formwrap">
      <div className="ac-rules-block-head">
        <div>
          <h3 className="ac-rules-block-title">Neue Benachrichtigungsregel</h3>
          <p className="ac-rules-block-subtitle">
            Lege fest, wann automatisch eine Benachrichtigung ausgelöst werden soll.
          </p>
        </div>

        <AceAvatar variant="motiviert1" size={42} />
      </div>

      <div className="ac-rules-formgrid">
        <div className="ac-rules-form-main">
          <div className="ac-rules-field">
            <label className="ac-rules-label">Titel</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder='z. B. "Mehrere Streak-Abbrüche in einer Klasse"'
              className="ac-rules-input"
            />
          </div>

          <div className="ac-rules-form-2col">
            <div className="ac-rules-field">
              <label className="ac-rules-label">Ereignis-Typ</label>
              <select
                value={form.type}
                onChange={(e) => update("type", e.target.value)}
                className="ac-select w-full"
              >
                <option value="" disabled>
                  Ereignis wählen…
                </option>
                <option value="Kritische Werte in einer Klasse">
                  Kritische Werte in einer Klasse
                </option>
                <option value="Einzelperson länger kritisch">
                  Einzelperson länger kritisch
                </option>
                <option value="Mehrere Streak-Abbrüche">
                  Mehrere Streak-Abbrüche
                </option>
                <option value="Viele offene Feedbacks">
                  Viele offene Feedbacks
                </option>
                <option value="Längere Inaktivität">
                  Längere Inaktivität
                </option>
                <option value="Sonstiges">
                  Sonstiges
                </option>
              </select>
            </div>

            <div className="ac-rules-field">
              <label className="ac-rules-label">Schwellenwert</label>
              <select
                value={form.threshold}
                onChange={(e) => update("threshold", e.target.value)}
                className="ac-select w-full"
              >
                <option value="" disabled>
                  Wert wählen…
                </option>
                <option value="ab 2 Personen">ab 2 Personen</option>
                <option value="ab 3 Personen">ab 3 Personen</option>
                <option value="ab 5 Personen">ab 5 Personen</option>
                <option value="ab 20 %">ab 20 %</option>
                <option value="ab 25 %">ab 25 %</option>
                <option value="ab 30 %">ab 30 %</option>
                <option value="ab 3 Tagen">ab 3 Tagen</option>
                <option value="ab 7 Tagen">ab 7 Tagen</option>
              </select>
            </div>
          </div>

          <div className="ac-rules-field">
            <label className="ac-rules-label">Kurzbeschreibung</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Beschreibe kurz, wann diese Regel relevant ist und worauf sie aufmerksam machen soll…"
              className="ac-rules-textarea"
            />
          </div>
        </div>

        <div className="ac-rules-form-side">
          <div className="ac-soft-panel ac-rules-previewcard">
            <div className="ac-rules-block-title">Vorschau</div>

            <div className="mt-4 space-y-3 text-sm text-ac-text">
              <div>
                <span className="font-extrabold">Titel:</span> {form.title || "—"}
              </div>
              <div>
                <span className="font-extrabold">Typ:</span> {form.type || "—"}
              </div>
              <div>
                <span className="font-extrabold">Schwellenwert:</span>{" "}
                {form.threshold || "—"}
              </div>
              <div>
                <span className="font-extrabold">Beschreibung:</span>{" "}
                {form.description || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ac-rules-actions-row">
        <button type="button" onClick={onSave} className="ac-primarybtn ac-rules-smallbtn">
          Speichern
        </button>

        <button type="button" onClick={onCancel} className="ac-ghostbtn ac-rules-smallbtn">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ open, ruleTitle, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="ac-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ac-modal">
        <div className="ac-modal-title">Regel entfernen?</div>
        <div className="ac-modal-text">
          Willst du die Regel{" "}
          <span className="font-extrabold text-ac-text">„{ruleTitle}“</span>{" "}
          wirklich entfernen?
        </div>

        <div className="ac-modal-actions">
          <button type="button" onClick={onCancel} className="ac-ghostbtn">
            Abbrechen
          </button>
          <button type="button" onClick={onConfirm} className="ac-primarybtn">
            Entfernen
          </button>
        </div>
      </div>
    </div>
  );
}

function DisableConfirmModal({ open, ruleTitle, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="ac-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ac-modal">
        <div className="ac-modal-title">Benachrichtigung wirklich ausschalten?</div>
        <div className="ac-modal-text">
          Die Regel{" "}
          <span className="font-extrabold text-ac-text">„{ruleTitle}“</span>{" "}
          sendet dann keine automatische E-Mail mehr. Willst du sie wirklich deaktivieren?
        </div>

        <div className="ac-modal-actions">
          <button type="button" onClick={onCancel} className="ac-ghostbtn">
            Abbrechen
          </button>
          <button type="button" onClick={onConfirm} className="ac-primarybtn">
            Ausschalten
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RulesPage() {
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);

  const [rules, setRules] = useState([
  {
    id: "r1",
    title: "Mehrere kritische Stresswerte in einer Klasse",
    description:
      "Wird ausgelöst, wenn in einer Klasse mehrere kritische Stresswerte gleichzeitig erkannt werden und ein rascher Blick auf die Situation sinnvoll ist.",
    enabled: true,
    iconType: "class",
    fixed: false,
    createdBy: "System",
  },
  {
    id: "r2",
    title: "Einzelperson über mehrere Tage kritisch",
    description:
      "Wird ausgelöst, wenn eine Person über mehrere Tage hintereinander auffällige Werte zeigt und genauer beobachtet werden sollte.",
    enabled: true,
    iconType: "person",
    fixed: false,
    createdBy: "System",
  },
  {
    id: "r3",
    title: "Mehrere Streak-Abbrüche in kurzer Zeit",
    description:
      "Wird ausgelöst, wenn in einer Klasse mehrere aktive Lernstreaks abbrechen. Das kann auf Motivationseinbruch oder Überforderung hindeuten.",
    enabled: true,
    iconType: "streak",
    fixed: false,
    createdBy: "System",
  },
  {
    id: "r4",
    title: "Viele offene Feedbacks ohne Antwort",
    description:
      "Wird ausgelöst, wenn mehrere Feedback-Nachrichten offen bleiben und eine Rückmeldung an Nutzer:innen verzögert wird.",
    enabled: false,
    iconType: "mail",
    fixed: false,
    createdBy: "System",
  },
  {
    id: "r5",
    title: "Systemausfall oder Synchronisationsfehler",
    description:
      "Diese Pflichtregel informiert immer über technische Störungen, Synchronisationsprobleme oder Ausfälle im System und kann nicht deaktiviert oder entfernt werden.",
    enabled: true,
    iconType: "system",
    fixed: true,
    createdBy: "System",
  },
]);

  const [form, setForm] = useState({
    title: "",
    type: "",
    description: "",
    threshold: "",
  });

  const aceVariant = useMemo(() => {
    const enabledCount = rules.filter((r) => r.enabled).length;
    if (enabledCount >= 4) return "motiviert2";
    if (enabledCount >= 2) return "ruhig2";
    return "ruhig1";
  }, [rules]);

  const activeRules = useMemo(() => rules.filter((rule) => rule.enabled).length, [rules]);
  const streakRules = useMemo(
    () => rules.filter((rule) => rule.iconType === "streak").length,
    [rules]
  );

  const requestToggleRule = (rule) => {
    if (rule.fixed) return;

    if (rule.enabled) {
      setDisableTarget(rule);
      return;
    }

    setRules((prev) =>
      prev.map((entry) =>
        entry.id === rule.id ? { ...entry, enabled: true } : entry
      )
    );
  };

  const confirmDisableRule = () => {
    if (!disableTarget) return;

    setRules((prev) =>
      prev.map((entry) =>
        entry.id === disableTarget.id ? { ...entry, enabled: false } : entry
      )
    );
    setDisableTarget(null);
  };

  const requestDeleteRule = (rule) => {
    if (rule.fixed) return;
    setDeleteTarget(rule);
  };

  const confirmDeleteRule = () => {
    if (!deleteTarget) return;
    setRules((prev) => prev.filter((rule) => rule.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const saveRule = () => {
    if (!form.title || !form.type || !form.description || !form.threshold) {
      alert("Bitte fülle alle Felder aus.");
      return;
    }

    const iconType =
      form.type === "Kritische Werte in einer Klasse"
        ? "class"
        : form.type === "Einzelperson länger kritisch"
        ? "person"
        : form.type === "Mehrere Streak-Abbrüche"
        ? "streak"
        : form.type === "Längere Inaktivität"
        ? "idle"
        : "mail";

    const newRule = {
      id: `r${Date.now()}`,
      title: form.title,
      description: `${form.description} Auslösung: ${form.threshold}.`,
      enabled: true,
      iconType,
      fixed: false,
      createdBy: "Admin"
    };

    setRules((prev) => [...prev, newRule]);
    setForm({
      title: "",
      type: "",
      description: "",
      threshold: "",
    });
    setIsCreating(false);
  };

  return (
    <DashboardLayout title="Benachrichtigungen · Regeln">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-rules-hero">
          <div className="ac-rules-hero-grid">
            <div>
              <h2 className="ac-rules-hero-title">Benachrichtigungsregeln</h2>
              <p className="ac-rules-hero-text">
                Hier legst du fest, bei welchen kritischen Situationen automatisch eine
                E-Mail an das Admin-Team gesendet werden soll. Systemrelevante Regeln
                bleiben aus Sicherheitsgründen immer aktiv.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="ac-content-inline-badge">
                  Aktive Regeln: {activeRules}
                </span>
                <span className="ac-content-inline-badge">
                  Streak-Regeln: {streakRules}
                </span>
                <span className="ac-content-inline-badge">
                  Pflichtregeln: {rules.filter((rule) => rule.fixed).length}
                </span>
              </div>
            </div>

            <div className="ac-rules-hero-side">
              <div className="ac-rules-acebox">
                <AceAvatar variant={aceVariant} size={56} />
                <div className="ac-rules-acebubble">
                  Ace sagt: Gute Regeln alarmieren nur dann, wenn wirklich etwas
                  wichtig ist – und sie behalten auch Streak-Veränderungen im Blick.
                </div>
              </div>
            </div>
          </div>
        </section>

        {!isCreating ? (
          <>
            <section className="ac-rules-list">
              {rules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onToggle={() => requestToggleRule(rule)}
                  onDelete={() => requestDeleteRule(rule)}
                />
              ))}
            </section>

            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="ac-soft-panel ac-rules-createbtn"
            >
              Neue Benachrichtigungsregel hinzufügen
            </button>
          </>
        ) : (
          <NewRuleForm
            form={form}
            setForm={setForm}
            onSave={saveRule}
            onCancel={() => setIsCreating(false)}
          />
        )}
      </div>

      <DeleteConfirmModal
        open={!!deleteTarget}
        ruleTitle={deleteTarget?.title || ""}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteRule}
      />

      <DisableConfirmModal
        open={!!disableTarget}
        ruleTitle={disableTarget?.title || ""}
        onCancel={() => setDisableTarget(null)}
        onConfirm={confirmDisableRule}
      />
    </DashboardLayout>
  );
}